import {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  collection,
  addDoc,
  onSnapshot,
  query,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import { getDB } from "./Firebase";
import { useAppSelector } from "./store/hooks";

interface ChatMessage {
  id: string;
  uid: string;
  nickname: string;
  text: string;
  ts: Timestamp | null;
}

const QUICK = ["Удачи!", "Хороший ход", "Ничего личного", "Везёт...", "Реванш?"];

type GameChatProps = {
  lobbyDocId: string;
  isOpen: boolean;
  onToggle: () => void;
};

const GameChat: FunctionComponent<GameChatProps> = ({ lobbyDocId, isOpen, onToggle }) => {
  const profile = useAppSelector((s) => s.user.profile);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput]       = useState("");
  const [unread, setUnread]     = useState(0);
  const bottomRef     = useRef<HTMLDivElement>(null);
  const wasOpen       = useRef(isOpen);
  const isInitialLoad = useRef(true);

  useEffect(() => {
    if (!lobbyDocId) return;
    const q = query(
      collection(getDB(), "lobbies", lobbyDocId, "chat"),
      orderBy("ts", "asc"),
      limit(60)
    );
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
      if (!wasOpen.current && !isInitialLoad.current) {
        const added = snap.docChanges().filter((c) => c.type === "added").length;
        if (added) setUnread((n) => n + added);
      }
      isInitialLoad.current = false;
    });
  }, [lobbyDocId]);

  useEffect(() => {
    wasOpen.current = isOpen;
    if (isOpen) {
      setUnread(0);
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    }
  }, [isOpen]);

  const send = async (text: string) => {
    const t = text.trim();
    if (!t || !profile) return;
    setInput("");
    try {
      await addDoc(collection(getDB(), "lobbies", lobbyDocId, "chat"), {
        uid:      profile.uid,
        nickname: profile.nickname,
        text:     t,
        ts:       serverTimestamp(),
      });
    } catch (_) {}
  };

  const fmt = (ts: Timestamp | null) => {
    if (!ts?.toDate) return "";
    const d = ts.toDate();
    return `${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`;
  };

  return (
    <>
      {/* Кнопка-таб */}
      <button
        type="button"
        className="Game-bottom-icon-btn Chat-tab-btn"
        onClick={onToggle}
        aria-label="Чат"
      >
        <svg
          width={20}
          height={20}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        {unread > 0 && !isOpen && <span className="Chat-tab-badge">{unread}</span>}
      </button>

      {/* Панель — оверлей сбоку, НЕ сдвигает контент */}
      {isOpen && (
        <>
          <div className="Chat-backdrop" onClick={onToggle} />
          <div className="Chat-panel">
            <div className="Chat-panel-header">
              <span className="Chat-panel-title">Чат</span>
              <button className="Chat-panel-close" onClick={onToggle}>&#x2715;</button>
            </div>

            <div className="Chat-messages">
              {messages.length === 0 && (
                <div className="Chat-empty">Начните разговор</div>
              )}
              {messages.map((m) => {
                const isMe = m.uid === profile?.uid;
                return (
                  <div key={m.id} className={`Chat-msg ${isMe ? "me" : "them"}`}>
                    {!isMe && <span className="Chat-msg-nick">{m.nickname}</span>}
                    <div className="Chat-msg-bubble">
                      <span className="Chat-msg-text">{m.text}</span>
                      <span className="Chat-msg-time">{fmt(m.ts)}</span>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <div className="Chat-quick">
              {QUICK.map((p) => (
                <button key={p} className="Chat-quick-btn" onClick={() => send(p)}>{p}</button>
              ))}
            </div>

            <div className="Chat-input-row">
              <input
                className="Chat-input"
                type="text"
                placeholder="Написать..."
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, 120))}
                onKeyDown={(e) => { if (e.key === "Enter") send(input); }}
                maxLength={120}
              />
              <button
                className="Chat-send-btn"
                onClick={() => send(input)}
                disabled={!input.trim()}
              >&#8593;</button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default GameChat;
